import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Retry a fetch request with exponential backoff
 * Useful for handling transient network errors and ad blocker interference
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries = 3,
  initialDelay = 1000
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const res = await fetch(url, options);

      // If successful or client error (4xx), return immediately
      // Don't retry client errors as they won't succeed on retry
      if (res.ok || (res.status >= 400 && res.status < 500)) {
        return res;
      }

      // For 5xx errors, retry
      lastError = new Error(`Server error: ${res.status} ${res.statusText}`);
    } catch (error) {
      // Network errors, CORS errors, ad blocker interference
      lastError = error instanceof Error ? error : new Error(String(error));

      // Log the error for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Retry] Attempt ${attempt + 1}/${maxRetries} failed:`, lastError.message);
      }
    }

    // If not the last attempt, wait before retrying
    if (attempt < maxRetries - 1) {
      const delay = initialDelay * Math.pow(2, attempt); // Exponential backoff
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Retry] Waiting ${delay}ms before retry...`);
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // All retries failed
  throw lastError || new Error('Request failed after all retries');
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
